import { Chip } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';
import { clampRoleColor } from '@/lib/roleColor';
import type { GuildRole } from '@/types/api';

/**
 * The "About" block of a user card — status text, bio, roles, and (in the full
 * dialog) the join date. Presentational; extracted from UserCard. Renders
 * nothing when there's nothing to show.
 */
export function UserCardProfileSections(props: {
  isDialog: boolean;
  joinedLabel: string | null;
  statusText: string | null;
  bio: string | null;
  roles: GuildRole[];
}) {
  const { isDialog, joinedLabel, statusText, bio, roles } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  if (!(statusText || bio || roles.length > 0 || (isDialog && joinedLabel))) return null;

  return (
    <div
      className={
        isDialog
          ? 'mx-4 mt-4 flex flex-col gap-3 rounded-xl border border-separator bg-default/30 px-4 py-3'
          : 'flex flex-col gap-2 border-t border-separator px-4 py-2.5'
      }
    >
      {isDialog && joinedLabel && (
        <div>
          <div className="font-medium mb-1 text-xs text-ink-muted">{t('prof.card.memberSince')}</div>
          <p className="text-xs leading-relaxed text-ink">{joinedLabel}</p>
        </div>
      )}
      {statusText && (
        <div>
          <div className="font-medium mb-1 text-xs text-ink-muted">{t('prof.card.statusLabel')}</div>
          <p className="text-xs leading-relaxed text-ink">{statusText}</p>
        </div>
      )}
      {bio && (
        <div>
          <div className="font-medium mb-1 text-xs text-ink-muted">{t('prof.card.bioLabel')}</div>
          <p className="whitespace-pre-line text-xs leading-relaxed text-ink-dim">{bio}</p>
        </div>
      )}
      {roles.length > 0 && (
        <div>
          <div className="font-medium mb-1.5 text-xs text-ink-muted">
            {t('prof.card.roles')}
          </div>
          <div className="flex flex-wrap gap-1">
            {roles.map((role) => {
              const display =
                clampRoleColor(`#${role.color.toString(16).padStart(6, '0')}`, theme) ?? undefined;
              return (
                <Chip
                  key={role.id}
                  size="sm"
                  variant="soft"
                  className="gap-1"
                  style={{
                    backgroundColor: display ? `${display}1F` : undefined,
                    color: display,
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: display ?? 'currentColor' }}
                  />
                  {role.name}
                </Chip>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
